import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { AdminStatsData, AdminStatsResponse, DashboardAction } from '@df-gate-share/shared';

const UsageStats: React.FC = () => {
  const [stats, setStats] = useState<AdminStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });

      const response = await apiClient.get(`/api/v1/admin/stats?${params.toString()}`);
      const data: AdminStatsResponse = response.data;
      setStats(data.stats);
    } catch (err) {
      setError('統計情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: string;
    color: string;
    subtitle?: string;
  }> = ({ title, value, icon, color, subtitle }) => (
    <div className="stat-card" style={{ borderLeftColor: color }}>
      <div className="flex justify-between items-center">
        <div>
          <div className="stat-card__label">{title}</div>
          <div className="stat-card__value">{value}</div>
          {subtitle && (
            <div className="stat-card__subtitle">{subtitle}</div>
          )}
        </div>
        <div><span className="material-symbols-outlined icon-lg">{icon}</span></div>
      </div>
    </div>
  );

  const getActionLabel = (action: DashboardAction): string => {
    const labels: Record<DashboardAction, string> = {
      'file.upload': 'アップロード',
      'file.delete': 'ファイル削除',
      'file.download': 'ダウンロード',
      'share.create': '共有作成',
      'share.download': '共有DL',
      'share.revoke': '共有無効化',
    };
    return labels[action] || action;
  };

  const getActionBadgeClass = (action: DashboardAction): string => {
    const classes: Record<DashboardAction, string> = {
      'file.upload': 'badge--info',
      'file.delete': 'badge--danger',
      'file.download': 'badge--success',
      'share.create': 'badge--active',
      'share.download': 'badge--success',
      'share.revoke': 'badge--warning',
    };
    return classes[action] || 'badge--info';
  };

  if (loading) {
    return (
      <div className="card card__body text-center">
        読み込み中...
      </div>
    );
  }

  if (error) {
    return (
      <div className="card card__body">
        <div className="alert alert--error">{error}</div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div>
      {/* Header */}
      <div className="card card__body mb-6">
        <h1 className="page-title">利用実績ダッシュボード</h1>
      </div>

      {/* Overview Cards */}
      <div className="mb-6" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--space-5)' }}>
        <StatCard
          title="アクティブユーザー数"
          value={stats.activeUsers}
          icon="group"
          color="var(--color-info)"
          subtitle="有効なユーザーアカウント"
        />
        <StatCard
          title="総ファイル数"
          value={stats.totalFiles}
          icon="folder"
          color="var(--color-success)"
          subtitle="保存中のファイル"
        />
        <StatCard
          title="総共有数"
          value={stats.totalShares}
          icon="link"
          color="var(--color-cyan-500)"
          subtitle="作成された共有リンク"
        />
        <StatCard
          title="ストレージ使用量"
          value={formatFileSize(stats.storageUsed)}
          icon="storage"
          color="var(--color-warning)"
          subtitle="S3バケット使用量"
        />
      </div>

      {/* Activity Stats */}
      <div className="stat-grid-3 mb-6" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-5)' }}>
        {/* Uploads */}
        <div className="card card__body">
          <div className="flex items-center gap-3 mb-6">
            <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>upload</span>
            <h3 className="font-semibold">アップロード</h3>
          </div>

          <div className="flex-col gap-3">
            <div className="summary-row">
              <span className="summary-row__label">今日</span>
              <span className="summary-row__value" style={{ color: 'var(--color-info)' }}>
                {stats.uploads.today}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-row__label">今週</span>
              <span className="summary-row__value" style={{ color: 'var(--color-info)' }}>
                {stats.uploads.thisWeek}
              </span>
            </div>
            <div className="summary-row" style={{ borderBottom: 'none' }}>
              <span className="summary-row__label">今月</span>
              <span className="summary-row__value" style={{ color: 'var(--color-info)' }}>
                {stats.uploads.thisMonth}
              </span>
            </div>
          </div>
        </div>

        {/* Shares */}
        <div className="card card__body">
          <div className="flex items-center gap-3 mb-6">
            <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>forward_to_inbox</span>
            <h3 className="font-semibold">共有作成</h3>
          </div>

          <div className="flex-col gap-3">
            <div className="summary-row">
              <span className="summary-row__label">今日</span>
              <span className="summary-row__value" style={{ color: 'var(--color-success)' }}>
                {stats.shares.today}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-row__label">今週</span>
              <span className="summary-row__value" style={{ color: 'var(--color-success)' }}>
                {stats.shares.thisWeek}
              </span>
            </div>
            <div className="summary-row" style={{ borderBottom: 'none' }}>
              <span className="summary-row__label">今月</span>
              <span className="summary-row__value" style={{ color: 'var(--color-success)' }}>
                {stats.shares.thisMonth}
              </span>
            </div>
          </div>
        </div>

        {/* Downloads */}
        <div className="card card__body">
          <div className="flex items-center gap-3 mb-6">
            <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>download</span>
            <h3 className="font-semibold">ダウンロード</h3>
          </div>

          <div className="flex-col gap-3">
            <div className="summary-row">
              <span className="summary-row__label">今日</span>
              <span className="summary-row__value" style={{ color: 'var(--color-cyan-500)' }}>
                {stats.downloads.today}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-row__label">今週</span>
              <span className="summary-row__value" style={{ color: 'var(--color-cyan-500)' }}>
                {stats.downloads.thisWeek}
              </span>
            </div>
            <div className="summary-row" style={{ borderBottom: 'none' }}>
              <span className="summary-row__label">今月</span>
              <span className="summary-row__value" style={{ color: 'var(--color-cyan-500)' }}>
                {stats.downloads.thisMonth}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card card__body">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-semibold" style={{ fontSize: 'var(--text-lg)' }}>
            最近の操作履歴
          </h3>

          {/* Date Range Filter */}
          <div className="flex items-center gap-3">
            <label className="text-sm text-muted">期間:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="form-input"
              style={{ width: 'auto' }}
            />
            <span className="text-muted">〜</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="form-input"
              style={{ width: 'auto' }}
            />
            <button
              onClick={fetchStats}
              className="btn btn--primary btn--sm"
            >
              更新
            </button>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>日時</th>
                <th>ユーザー</th>
                <th>操作</th>
                <th>詳細</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentActivity.map((activity, index) => (
                <tr key={index}>
                  <td>{formatDate(activity.timestamp)}</td>
                  <td style={{ color: 'var(--color-text-primary)' }}>{activity.userEmail}</td>
                  <td>
                    <span className={`badge ${getActionBadgeClass(activity.action)}`}>
                      {getActionLabel(activity.action)}
                    </span>
                  </td>
                  <td>{activity.description}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {stats.recentActivity.length === 0 && (
            <div className="empty-state">
              <div className="empty-state__desc">操作履歴がありません</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsageStats;
